import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { siteMap } from "internal-sitemap";
import PageRecord from "models/view-models/page-record";
import { RouteUtils } from "utilities/route-utils";
import { ToastManager } from "utilities/toast/toast-manager";
import useNotebookSidebar from "./sidebar/use-notebook-sidebar";
import { t } from "utilities/localization-utils";
import { useDeletePage } from "utilities/hooks/domain/notebooks/pages/use-delete-page";
import { CollectionUtils } from "utilities/collection-utils";
import useModalActions from "utilities/hooks/use-modal-actions";
import useCurrentPage from "utilities/hooks/domain/notebooks/pages/use-current-page";
import { useSharedPageContext } from "utilities/contexts/shared-page/shared-page-context";

const usePageActions = (page: PageRecord | null) => {
    const [isNewlyPublicSharedPage, setIsNewlyPublicSharedPage] =
        useState<boolean>(false);

    const {
        isOpen: isDeleteConfirmModalOpen,
        handleOpen: handleDeleteConfirmModalOpen,
        handleClose: handleDeleteConfirmModalClose,
    } = useModalActions();
    const {
        handleOpen: handleCopyPageModalOpen,
        isOpen: isCopyPageModalOpen,
        handleClose: handleCopyPageModalClose,
    } = useModalActions();
    const {
        handleClose: handlePageAccessModalClose,
        handleOpen: handlePageAccessModalOpen,
        isOpen: isPageAccessModalOpen,
    } = useModalActions();

    const {
        handleClose: handleRelocateModalClose,
        handleOpen: handleRelocateModalOpen,
        isOpen: isRelocateModalOpen,
    } = useModalActions();

    const {
        handleClose: handleEditPageModalClose,
        handleOpen: handleEditPageModalOpen,
        isOpen: isEditPageModalOpen,
    } = useModalActions();

    const {
        handleClose: handleManageCustomLogoModalClose,
        handleOpen: handleManageCustomLogoModalOpen,
        isOpen: isManageCustomLogoModalOpen,
    } = useModalActions();

    const { notebookRoute } = useNotebookSidebar();
    const history = useHistory();
    const { currentPage } = useCurrentPage();
    const { pageGuid, recipientGuid } = useSharedPageContext();

    const currentlyOnPage = useMemo(
        () => page?.id === currentPage?.id,
        [currentPage?.id, page?.id]
    );

    const handleGlobalPageShareLinkModalClose = () => {
        setIsNewlyPublicSharedPage(false);
    };

    const handleOnPrintClick = () => {
        const pageToPrint = pageGuid
            ? RouteUtils.getUrl(
                  siteMap.printable.publicNotebookPage,
                  {
                      pageGuid,
                  },
                  { recipient: recipientGuid }
              )
            : RouteUtils.getUrl(siteMap.printable.authenticatedNotebookPage, {
                  notebookId: page?.notebookId,
                  pageId: page?.id,
              });
        window.open(pageToPrint, "_blank");
    };

    const handleDeleteSuccess = () => {
        ToastManager.success(
            t("page-success-deletePage", { pageName: page?.name })
        );

        if (currentlyOnPage || !currentPage) {
            redirectToNotebook(page!.notebookId);
        } else {
            handleDeleteConfirmModalClose();
        }
    };

    const { deletePage, errors } = useDeletePage({
        page: page ?? undefined,
        onSuccess: handleDeleteSuccess,
    });

    const redirectToNotebook = (notebookId: number) => {
        history.push(notebookRoute(notebookId));
    };

    useEffect(() => {
        if (!CollectionUtils.hasValues(errors)) {
            return;
        }

        ToastManager.error(
            t("page-error-deletePage", { pageName: page?.name })
        );
    }, [errors, page?.name]);

    const handlePageAccessModalSave = (isNewlyPublic: boolean) => {
        if (isNewlyPublic) {
            setIsNewlyPublicSharedPage(true);
            return;
        }
        handlePageAccessModalClose();
    };

    return {
        deletePage,
        handleCopyPageModalClose,
        handleCopyPageModalOpen,
        handleDeleteConfirmModalClose,
        handleDeleteConfirmModalOpen,
        handleEditPageModalClose,
        handleEditPageModalOpen,
        handleGlobalPageShareLinkModalClose,
        handleManageCustomLogoModalClose,
        handleManageCustomLogoModalOpen,
        handleOnPrintClick,
        handlePageAccessModalClose,
        handlePageAccessModalOpen,
        handlePageAccessModalSave,
        handleRelocateModalClose,
        handleRelocateModalOpen,
        isCopyPageModalOpen,
        isDeleteConfirmModalOpen,
        isEditPageModalOpen,
        isManageCustomLogoModalOpen,
        isNewlyPublicSharedPage,
        isPageAccessModalOpen,
        isRelocateModalOpen,
    };
};

export default usePageActions;
